import { useCallback, useEffect, useState } from 'react'

export type URLSearchParamsInit =
  | string
  | string[][]
  | Record<string, string | string[]>
  | URLSearchParams

export interface NavigateOptions {
  replace?: boolean
}

export type SetURLSearchParams = (
  nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOpts?: NavigateOptions
) => void

function readCurrent(): URLSearchParams {
  return new URLSearchParams(window.location.search)
}

function resolveInit(init: URLSearchParamsInit): URLSearchParams {
  if (init instanceof URLSearchParams) return new URLSearchParams(init)
  if (typeof init === 'string' || Array.isArray(init)) {
    return new URLSearchParams(init)
  }
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, v)
    } else {
      sp.set(key, value)
    }
  }
  return sp
}

/**
 * Mirrors the `useSearchParams` hook from `react-router` so non-v2 pages
 * (Rails-mounted React islands without a router) can use the same shape.
 *
 * - Returns the current `window.location.search` as a `URLSearchParams`.
 * - `setSearchParams` pushes a history entry by default; pass `{ replace: true }`
 *   to swap the current entry instead.
 * - Re-renders consumers on `popstate` (browser Back/Forward).
 */
export function useSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const [searchParams, setSearchParamsState] = useState<URLSearchParams>(readCurrent)

  useEffect(() => {
    const handlePopState = () => {
      setSearchParamsState(readCurrent())
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const setSearchParams = useCallback<SetURLSearchParams>((nextInit, navigateOpts) => {
    const resolved = resolveInit(
      typeof nextInit === 'function' ? nextInit(readCurrent()) : (nextInit ?? '')
    )
    const search = resolved.toString()
    const nextUrl = search ? `?${search}` : window.location.pathname
    const currentFull = `${window.location.pathname}${window.location.search}`
    const nextFull = nextUrl.startsWith('?') ? `${window.location.pathname}${nextUrl}` : nextUrl
    if (currentFull === nextFull) return
    if (navigateOpts?.replace) {
      window.history.replaceState(null, '', nextUrl)
    } else {
      window.history.pushState(null, '', nextUrl)
    }
    setSearchParamsState(new URLSearchParams(resolved.toString()))
  }, [])

  return [searchParams, setSearchParams]
}
